/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------ * EC-TS: FIX THEME.JS NULL VARIANT ERROR
 * ------------------------------------------------------------------------------------------------------------
 * 
 * When auto-selecting performance levels during material changes, there's a brief moment where
 * the variant combination is invalid (null). Theme.js line 5543 doesn't handle null variants.
 * This patches theme.js by wrapping its problematic forEach to skip null variants.
 */

(function() {
  // Wait for DOM to ensure product-media elements exist
  document.addEventListener('DOMContentLoaded', function() {
    // Find all product-media elements and patch their _onVariantChanged method
    const productMediaElements = document.querySelectorAll('product-media');
    
    productMediaElements.forEach((element) => {
      if (element._onVariantChanged) {
        const originalMethod = element._onVariantChanged.bind(element);
        
        element._onVariantChanged = function(event) {
          const variant = event.detail?.variant;
          
          // Skip processing if variant is null/undefined
          if (!variant || variant === null || variant === undefined) {
            return;
          }
          
          // Call original method with valid variant
          return originalMethod(event);
        };
      }
    });
  });
})();

/**
 * ------------------------------------------------------------------------------------------------------------ * EXTEND PRODUCT RERENDER TO SUPPORT CUSTOM BLOCK TYPES
 * ------------------------------------------------------------------------------------------------------------
 * 
 * This extends the theme's product-rerender system to support additional block types
 * without modifying theme.js
 */

(function() {
  // Listen for product:rerender events on all forms
  document.addEventListener('product:rerender', function(event) {
    // Add custom block types that should be re-rendered
    const customBlockTypes = ['price'];
    const productRerenderElement = event.target.closest('product-rerender');
    
    if (!productRerenderElement || !event.detail.htmlFragment) {
      return;
    }
    
    // Get the matching element from the response HTML
    const matchingElement = event.detail.htmlFragment.querySelector(`#${productRerenderElement.id}`);
    if (!matchingElement) {
      return;
    }
    
    // Handle partial rerender for custom block types
    if (productRerenderElement.hasAttribute('allow-partial-rerender') && !event.detail.productChange) {
      customBlockTypes.forEach((blockType) => {
        productRerenderElement.querySelectorAll(`[data-block-type="${blockType}"]`).forEach((element) => {
          const blockId = element.getAttribute('data-block-id');
          const matchingBlock = matchingElement.querySelector(`[data-block-type="${blockType}"][data-block-id="${blockId}"]`);
          
          if (matchingBlock) {
            element.replaceWith(matchingBlock);
          }
        });
      });
    }
  }, true); // Use capture phase to run before theme's handler
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * ANIMATED GAUGE COMPONENT
 * ------------------------------------------------------------------------------------------------------------
 * 
 * SVG-based animated gauge component for displaying performance levels
 */

class AnimatedGauge {
  constructor(container, options = {}) {
    this.container = container;
    this.value = options.value || 0;
    this.maxValue = options.maxValue || 10;
    this.label = options.label || "OE Stock";
    this.subtitle = options.subtitle || "";
    this.animationDuration = options.animationDuration || 1000;
    this.animatedValue = 0;
    this.animationFrame = null;
    
    this.render();
  }
  
  render() {
    // SVG center and radius
    const cx = 130;
    const cy = 130;
    const radius = 110;
    const strokeWidth = 10;
    
    // Gauge angles - 135° to 405° (270° sweep)
    const startAngle = 135;
    const endAngle = 405;
    const angleRange = endAngle - startAngle;
    
    // Helper to convert angle to coordinates
    const angleToCoords = (angle, r = radius) => {
      const rad = (angle * Math.PI) / 180;
      return {
        x: cx + r * Math.cos(rad),
        y: cy + r * Math.sin(rad)
      };
    };
    
    // Create arc path
    const createArc = (startA, endA, r = radius) => {
      const start = angleToCoords(startA, r);
      const end = angleToCoords(endA, r);
      const largeArcFlag = endA - startA > 180 ? 1 : 0;
      return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
    };
    
    // Create number positions
    const valueToAngle = (val) => startAngle + (val / this.maxValue) * angleRange;
    let numbersHTML = '';
    for (let i = 0; i <= 10; i++) {
      const angle = valueToAngle(i);
      const pos = angleToCoords(angle, radius - 23);
      numbersHTML += `<text x="${pos.x}" y="${pos.y}" text-anchor="middle" dominant-baseline="middle" fill="black" font-size="16" font-family="system-ui, -apple-system, sans-serif" font-weight="400">${i}</text>`;
    }
    
    // Create SVG
    const svg = `
      <svg width="260" height="220" viewBox="0 0 260 220" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient
            id="gaugeGradient-${this.label.replace(/\s+/g, '-')}"
            gradientUnits="userSpaceOnUse"
            gradientTransform="rotate(135 130 130)"
            x1="0" y1="130" x2="260" y2="130">
            <stop offset="0%" stop-color="#B10512"/>
            <stop offset="55%" stop-color="#D84A4F"/>
            <stop offset="100%" stop-color="#F6E7E7"/>
          </linearGradient>



        </defs>
        
        <!-- Background arc (gray) -->
        <path d="${createArc(startAngle, endAngle)}" stroke="#C5C5C5" stroke-width="${strokeWidth}" stroke-linecap="round" fill="none" />
        
        <!-- Filled arc (red gradient) -->
        <path class="gauge-fill" d="${createArc(startAngle, startAngle)}" stroke="url(#gaugeGradient-${this.label.replace(/\s+/g, '-')})" stroke-width="${strokeWidth}" stroke-linecap="round" fill="none" />
        
        <!-- Numbers -->
        ${numbersHTML}
        
        <!-- Pointer -->
        <g class="gauge-pointer" transform="translate(${cx}, ${cy}) rotate(${startAngle + 90})">
          <path d="M-4.5 0 L0 -70 L4.5 0 Z" fill="#383838" />
        </g>
        
        <!-- Center circle -->
        <circle cx="${cx}" cy="${cy}" r="14.5" fill="#383838" stroke="black" stroke-width="5" />
        
        <!-- Label -->
        <text x="${cx}" y="${cy + 49}" text-anchor="middle" fill="#D5161D" font-size="18" font-family="system-ui, -apple-system, sans-serif" font-weight="900">${this.label}</text>
      </svg>
      ${this.subtitle ? `<div class="performance-gauge__subtitle">${this.subtitle}</div>` : ''}
    `;
    
    this.container.innerHTML = svg;
    this.fillPath = this.container.querySelector('.gauge-fill');
    this.pointerGroup = this.container.querySelector('.gauge-pointer');
    this.angleToCoords = angleToCoords;
    this.createArc = createArc;
    this.valueToAngle = valueToAngle;
    this.startAngle = startAngle;
    this.cx = cx;
    this.cy = cy;
  }
  
  animateTo(targetValue) {
    // Cancel any ongoing animation
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    const startTime = Date.now();
    const startValue = this.animatedValue;
    const endValue = Math.min(Math.max(targetValue, 0), this.maxValue);
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / this.animationDuration, 1);
      
      // Easing function for smooth animation (ease-out-cubic)
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      this.animatedValue = startValue + (endValue - startValue) * easeOutCubic;
      
      // Update the gauge visuals
      const currentAngle = this.valueToAngle(this.animatedValue);
      
      // Update fill arc
      if (this.animatedValue > 0) {
        this.fillPath.setAttribute('d', this.createArc(this.startAngle, currentAngle));
        this.fillPath.style.opacity = '1';
      } else {
        this.fillPath.style.opacity = '0';
      }
      
      // Update pointer rotation
      const pointerRotation = currentAngle + 90;
      this.pointerGroup.setAttribute('transform', `translate(${this.cx}, ${this.cy}) rotate(${pointerRotation})`);
      
      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      }
    };
    
    this.animationFrame = requestAnimationFrame(animate);
  }
}

/**
 * Performance Bar Animation
 * Animates rectangular bars with staggered fill animation
 */
function animatePerformanceBar(barElement, targetValue) {
  if (!barElement) return;
  
  const rectangles = barElement.querySelectorAll('.performance-bar__rectangle');
  const maxBars = rectangles.length; // Usually 10
  const barsToFill = Math.min(Math.max(Math.round(targetValue), 0), maxBars);
  
  // Reset all rectangles first
  rectangles.forEach(rect => rect.classList.remove('filled'));
  
  // Animate bars one by one with stagger
  for (let i = 0; i < barsToFill; i++) {
    setTimeout(() => {
      rectangles[i].classList.add('filled');
    }, i * 100); // 100ms delay between each bar
  }
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * PERFORMANCE LEVEL DISPLAY
 * ------------------------------------------------------------------------------------------------------------
 * 
 * This code handles updating the performance level display when a variant changes.
 * It looks for products with a "Performance Level" option and displays the variant's performance_scale
 * metafield value alongside the OE Stock value.
 */

let lastDisplayedVariantId = null;
let lastClickedVariantId = null;
let gaugeInstances = {};

function updatePerformanceDisplay(variant) {
  // Find the performance level display container
  const performanceDisplay = document.querySelector('[data-performance-container]');
  
  if (!performanceDisplay) {
    return;
  }

  // If variant is null, just return without hiding
  // The gauge will stay visible showing the last valid selection
  if (!variant) {
    return;
  }
  
  // Get the variant metafields data from the JSON script tag
  const metafieldsScript = document.querySelector('script[data-variant-metafields]');
  let performanceScale = null;
  
  if (metafieldsScript && variant && variant.id) {
    try {
      const metafieldsData = JSON.parse(metafieldsScript.textContent);
      const variantMetafields = metafieldsData[variant.id];
      
      if (variantMetafields && variantMetafields.performance_scale) {
        performanceScale = variantMetafields.performance_scale;
      }
    } catch (e) {
      console.error('Error parsing variant metafields:', e);
    }
  }
  
  if (performanceScale) {
    // Update last displayed variant
    lastDisplayedVariantId = variant.id;
      // Ensure display is shown first (before initializing gauges/bars)
      performanceDisplay.style.display = 'block';
      
      // Check if we're using bars or gauges
      const usingBars = performanceDisplay.querySelector('.performance-level__bars') !== null;
      
      if (usingBars) {
        // Handle bar visualization
        const oeStockBar = performanceDisplay.querySelector('[data-bar="oe-stock"]');
        const valveClosedBar = performanceDisplay.querySelector('[data-bar="valve-closed"]');
        const valveOpenedBar = performanceDisplay.querySelector('[data-bar="valve-opened"]');
        
        // Get the performance level option value (e.g., "SGT Lightweight Performance", "GTC-VC VALVED CONTROL")
        // Extract the first word to use as the bar label
        let performanceLevelLabel = '';
        if (variant && variant.option2) {
          // Assuming Performance Level is option2 - extract first word
          performanceLevelLabel = variant.option2.split(' ')[0];
        }
        
        // Check if it's a range or single value
        const dashPattern = /[-–—]/;
        const isRange = dashPattern.test(performanceScale);
        
        if (isRange) {
          // Parse range values
          const [closedValue, openedValue] = performanceScale.split(dashPattern).map(v => parseFloat(v.trim()));
          
          // Show both range bars
          if (valveClosedBar) {
            valveClosedBar.style.display = 'flex';
            valveClosedBar.setAttribute('data-bar-value', closedValue);
            const titleElement = valveClosedBar.querySelector('.performance-bar__title');
            if (titleElement) titleElement.textContent = 'Valve Closed';
            animatePerformanceBar(valveClosedBar, closedValue);
          }
          if (valveOpenedBar) {
            valveOpenedBar.style.display = 'flex';
            valveOpenedBar.setAttribute('data-bar-value', openedValue);
            animatePerformanceBar(valveOpenedBar, openedValue);
          }
        } else {
          // Single value
          const singleValue = parseFloat(performanceScale.toString().trim());
          
          if (valveClosedBar && !isNaN(singleValue)) {
            valveClosedBar.style.display = 'flex';
            valveClosedBar.setAttribute('data-bar-value', singleValue);
            // Update the title to use the first word from the variant option
            const titleElement = valveClosedBar.querySelector('.performance-bar__title');
            if (titleElement && performanceLevelLabel) {
              titleElement.textContent = performanceLevelLabel;
            }
            animatePerformanceBar(valveClosedBar, singleValue);
          }
          if (valveOpenedBar) {
            valveOpenedBar.style.display = 'none';
          }
        }
        
        // Animate OE Stock bar
        if (oeStockBar) {
          const oeValue = parseFloat(oeStockBar.dataset.barValue);
          if (!isNaN(oeValue)) {
            animatePerformanceBar(oeStockBar, oeValue);
          }
        }
      } else {
        // Handle gauge visualization (existing code)
        const oeStockGauge = performanceDisplay.querySelector('[data-gauge="oe-stock"]');
        const valveClosedGauge = performanceDisplay.querySelector('[data-gauge="valve-closed"]');
        const valveOpenedGauge = performanceDisplay.querySelector('[data-gauge="valve-opened"]');
      
        // console.log('Gauge elements found:', {
        //   oeStockGauge: !!oeStockGauge,
        //   valveClosedGauge: !!valveClosedGauge,
        //   valveOpenedGauge: !!valveOpenedGauge
        // });
        
        // Check if it's a range (e.g., "2-4", "3—8", "2–4") or single value (e.g., "3")
        // Support all dash types: hyphen (-), en dash (–), em dash (—)
        const dashPattern = /[-–—]/;
        const isRange = dashPattern.test(performanceScale);
        
        // console.log('Is range:', isRange, 'Performance scale:', performanceScale);
        
        // Get the performance level option value (e.g., "SGT Lightweight Performance", "GTC-VC VALVED CONTROL")
        // Extract the first word to use as the gauge label
        let performanceLevelLabel = '';
        if (variant && variant.option2) {
          // Assuming Performance Level is option2 - extract first word
          performanceLevelLabel = variant.option2.split(' ')[0];
        }
        
        if (isRange) {
          // Parse range values - split on any dash type
          const [closedValue, openedValue] = performanceScale.split(dashPattern).map(v => parseFloat(v.trim()));
          
          // Hide single gauge, show both range gauges
          if (valveClosedGauge) {
            valveClosedGauge.style.display = 'block';
            initializeOrUpdateGauge(valveClosedGauge, closedValue, performanceLevelLabel, 'Valve Closed');
          }
          if (valveOpenedGauge) {
            valveOpenedGauge.style.display = 'block';
            initializeOrUpdateGauge(valveOpenedGauge, openedValue, performanceLevelLabel, 'Valve Opened');
          }
        } else {
          // Single value - use valve closed gauge, hide opened gauge
          const singleValue = parseFloat(performanceScale.toString().trim());
          
          if (valveClosedGauge && !isNaN(singleValue)) {
            valveClosedGauge.style.display = 'block';
            initializeOrUpdateGauge(valveClosedGauge, singleValue, performanceLevelLabel, '');
          }
          if (valveOpenedGauge) {
            valveOpenedGauge.style.display = 'none';
            // Clear the gauge instance if it exists
            delete gaugeInstances['valve-opened'];
            valveOpenedGauge.innerHTML = '';
          }
        }
        
        // Initialize/update OE Stock gauge
        if (oeStockGauge) {
          const oeValue = parseFloat(oeStockGauge.dataset.gaugeValue);
          if (!isNaN(oeValue)) {
            initializeOrUpdateGauge(oeStockGauge, oeValue, 'OE Stock', '');
          }
        }
      }
      
      // Show the display with animation
      // Remove and re-add to force CSS animation to play
      performanceDisplay.classList.remove('is-visible');
      performanceDisplay.offsetHeight; // Force reflow
      performanceDisplay.classList.add('is-visible');
  } else {
    // Fade out if no performance_scale metafield
    performanceDisplay.classList.remove('is-visible');
    lastDisplayedVariantId = null;
  }
}

function initializeOrUpdateGauge(container, value, label, subtitle) {
  const gaugeId = container.dataset.gauge;
  
  // Check if container is empty or if gauge instance doesn't match container
  const needsRecreation = !gaugeInstances[gaugeId] || 
                          !container.querySelector('svg') ||
                          gaugeInstances[gaugeId].label !== label ||
                          gaugeInstances[gaugeId].subtitle !== subtitle;
  
  if (needsRecreation) {
    // Create new gauge instance
    gaugeInstances[gaugeId] = new AnimatedGauge(container, {
      value: 0,
      maxValue: 10,
      label: label,
      subtitle: subtitle || '',
      animationDuration: 1000
    });
  } else {
    // Reset to 0 before animating to new value
    gaugeInstances[gaugeId].animatedValue = 0;
    if (gaugeInstances[gaugeId].fillPath) {
      gaugeInstances[gaugeId].fillPath.style.opacity = '0';
    }
    if (gaugeInstances[gaugeId].pointerGroup) {
      gaugeInstances[gaugeId].pointerGroup.setAttribute('transform', 
        `translate(${gaugeInstances[gaugeId].cx}, ${gaugeInstances[gaugeId].cy}) rotate(${gaugeInstances[gaugeId].startAngle + 90})`
      );
    }
  }
  
  // Animate to the new value
  gaugeInstances[gaugeId].animateTo(value);
}

// Function to get currently selected variant based on form inputs
function getCurrentVariant() {
  const variantScript = document.querySelector('script[data-variant]');
  if (!variantScript) return null;
  
  try {
    return JSON.parse(variantScript.textContent);
  } catch (e) {
    // console.error('Error parsing variant:', e);
    return null;
  }
}

// Function to update price display (backup - theme should handle this)
function updatePriceDisplay(variant) {
  if (!variant || !variant.price) {
    return;
  }
  
  // Find the price container
  const priceContainer = document.querySelector('[data-block-type="price"] .price-list');
  if (!priceContainer) {
    return;
  }
  
  // Format price (price is in cents)
  const price = (variant.price / 100).toFixed(2);
  const compareAtPrice = variant.compare_at_price ? (variant.compare_at_price / 100).toFixed(2) : null;
  
  // Update sale price
  const salePrice = priceContainer.querySelector('.price--highlight');
  if (salePrice) {
    salePrice.innerHTML = `<span class="visually-hidden">Sale price</span>$${price}`;
  }
  
  // Update or hide compare at price
  const comparePrice = priceContainer.querySelector('.price--compare');
  if (compareAtPrice && compareAtPrice > price) {
    if (comparePrice) {
      comparePrice.innerHTML = `<span class="visually-hidden">Regular price</span>$${compareAtPrice}`;
      comparePrice.style.display = '';
    }
  } else if (comparePrice) {
    comparePrice.style.display = 'none';
  }
}

// Listen for variant changes
document.addEventListener('variant:changed', function(event) {
  //console.log('=== VARIANT CHANGED EVENT ===');
  //console.log('Event detail:', event.detail);
  //console.log('Variant object:', event.detail.variant);
  //console.log('Variant ID:', event.detail.variant?.id);
  //console.log('Variant price:', event.detail.variant?.price);
  //console.log('Variant title:', event.detail.variant?.title);
  //console.log('Event target (form):', event.target);
  //console.log('=============================');
  
  const variant = event.detail.variant;
  
  // Use setTimeout to ensure DOM is stable after any rerenders
  setTimeout(function() {
    updatePerformanceDisplay(variant);
  }, 50);
  
  // Manually update price (workaround for theme.js error)
  if (variant) {
    updatePriceDisplay(variant);
  }
  
  // Update Add to Cart button price if enabled
  if (variant) {
    updateButtonPrice(variant);
  }
});

// Function to update the Add to Cart button with the sale price
function updateButtonPrice(variant) {
  const addToCartButton = document.querySelector('button[data-product-add-to-cart-button]');
  
  if (!addToCartButton || !addToCartButton.hasAttribute('data-show-price-in-button')) {
    return;
  }
  
  // Only update if variant is available (not sold out)
  if (!variant || !variant.available) {
    return;
  }
  
  // Find the price span in the button
  let priceSpan = addToCartButton.querySelector('.button-price');
  
  // Get the price from the price block to ensure we use the sale price
  const priceBlock = document.querySelector('[data-block-type="price"]');
  if (!priceBlock) {
    return;
  }
  
  const salePriceElement = priceBlock.querySelector('.price--highlight, .price:not(.price--compare)');
  if (!salePriceElement) {
    return;
  }
  
  // Extract just the price text (without the "Sale price" or other text)
  const priceText = salePriceElement.textContent.trim().replace(/Sale price|Regular price/gi, '').trim();
  
  // Update or create the price span
  if (priceSpan) {
    priceSpan.innerHTML = '&nbsp;– ' + priceText;
  } else {
    // Create the price span if it doesn't exist
    priceSpan = document.createElement('span');
    priceSpan.className = 'button-price';
    priceSpan.innerHTML = '&nbsp;– ' + priceText;
    addToCartButton.appendChild(priceSpan);
  }
}

// No need for rerender listeners - variant:changed fires after rerender with correct timing

// Initialize on page load
document.addEventListener('DOMContentLoaded', function() {
  // Show performance gauge on page load if a variant is selected
  const initialVariant = getCurrentVariant();
  if (initialVariant) {
    updatePerformanceDisplay(initialVariant);
  }
  
  // Initialize button price if setting is enabled
  const addToCartButton = document.querySelector('button[data-product-add-to-cart-button]');
  if (addToCartButton && addToCartButton.hasAttribute('data-show-price-in-button')) {
    const initialVariant = getCurrentVariant();
    if (initialVariant) {
      updateButtonPrice(initialVariant);
    }
  }
});